import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import type { ReactNode } from 'react'
import Sidebar, { COLLAPSED_WIDTH, EXPANDED_WIDTH } from './Sidebar'
import Navbar from './Navbar'
import FlowChatLauncher from '@/components/chat/FlowChatLauncher'
import { layout } from '../../theme/muiTheme'
import { NavbarProvider } from '@/contexts/NavbarProvider'
import { useNavbar } from '@/contexts/NavbarContext'

interface LayoutProps {
  children: ReactNode
  showSidebar?: boolean
  showBackButton?: boolean
  userName?: string
}

function LayoutContent({
  children,
  showSidebar = true,
  showBackButton = true,
  userName,
}: LayoutProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const [sidebarOpen] = useState(true)
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const { heightOffset, backButton, panelOffset, panelRightOffset } = useNavbar()
  const effectiveShowBack = showBackButton && backButton !== 'hide'

  const handleMobileMenuClick = () => {
    setMobileDrawerOpen(true)
  }

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {showSidebar && (
        <Sidebar
          open={isMobile ? mobileDrawerOpen : sidebarOpen}
          onExpandedChange={(expanded) => setSidebarExpanded(expanded)}
          onClose={handleMobileDrawerClose}
          variant={isMobile ? 'temporary' : 'permanent'}
          showBackButton={effectiveShowBack}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width:
            showSidebar && sidebarOpen && !isMobile
              ? `calc(100vw - ${sidebarExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH})`
              : '100vw',
          bgcolor: 'background.default',
          marginLeft:
            showSidebar && sidebarOpen && !isMobile
              ? sidebarExpanded
                ? EXPANDED_WIDTH
                : COLLAPSED_WIDTH
              : 0,
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Navbar
          showBackButton={effectiveShowBack}
          userName={userName}
          showMenuButton={isMobile && showSidebar}
          onMenuClick={handleMobileMenuClick}
          // The app rail only. A page-registered panel no longer pushes the
          // navbar aside: the bar runs the full width and the panel starts
          // BENEATH it. Beside it, the panel's own collapse chevron sat
          // directly under the navbar's back chevron and read as the same
          // control -- two arrows an inch apart doing unrelated things.
          sidebarOffset={
            showSidebar && sidebarOpen && !isMobile
              ? sidebarExpanded
                ? EXPANDED_WIDTH
                : COLLAPSED_WIDTH
              : '0px'
          }
          // Where the page's own content starts, so the bar can line its title
          // up with the column underneath instead of centring on a different
          // box. Not a shift -- an inset.
          contentInset={isMobile ? 0 : panelOffset}
          // A right-hand detail drawer is fixed and would sit ON TOP of the
          // navbar; ending the bar before it is what makes the page shrink as
          // one piece. Mobile panels are fullscreen dialogs, so no offset.
          rightOffset={`${isMobile ? 0 : panelRightOffset}px`}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            pt: `${layout.navbarHeight + heightOffset}px`,
            transition: 'padding-top 0.2s ease',
          }}
        >
          {children}
        </Box>
      </Box>
      {showSidebar && <FlowChatLauncher />}
    </Box>
  )
}

export default function Layout(props: LayoutProps) {
  return (
    <NavbarProvider>
      <LayoutContent {...props} />
    </NavbarProvider>
  )
}
